import { DomSanitizer } from "@angular/platform-browser";
import { MultiSelectChipOption } from "src/app/components/multi-select-chips/multi-select-chips.component";
import { translateAndFormat } from "src/app/i18next";
import { WorkPeriod } from "src/app/models/mir-rule.model";
import { DisplayTimezonePipe } from "src/app/pipes/display-timezone.pipe";
import { WizardId, WizardService } from "../../../services/wizard/wizard.service";
import { makeKey, TripFrequencyUi, TripSemantics } from "../../create-rule/define-duty/define-duty.component";
import { StepProps } from "../../create-rule/step-props";
import { arrayFilterMap } from "../util/array-filter-map";
import { ValidatedElement } from "../validated-element";
import { extraHtml } from "./extra-html/workperiods.extra-html";

const leftSide = [StepProps.DefineDuty_InspectionTypes, StepProps.DefineDuty_StartCondition];
const rightSide = [StepProps.DefineDuty_Timezone, StepProps.DefineDuty_WorkPeriods];

function isMissingAndRequired(subjectProp: StepProps, wizardService: WizardService, tripSemantics: TripSemantics) {
	let isRequired = false;

	// local util functions
	function valueExists(stepProp: StepProps, val: any) {
		const exists =
			(val && (typeof val === "string" || typeof val === "number")) ||
			(Array.isArray(val) && val.filter(arrayFilterMap[stepProp] ?? (_ => true)).length > 0);

		return exists;
	}

	function isLeftSide(prop1: StepProps) {
		return !!leftSide.find(p => p === prop1);
	}
	function hasValues(side: StepProps[], wizardService: WizardService, tripSemantics: TripSemantics) {
		let _hasValues = false;

		side.forEach(stepProp => {
			const propValue = wizardService.getValue(WizardId.createRule, makeKey(stepProp, tripSemantics));

			if (valueExists(stepProp, propValue)) {
				_hasValues = true;
			}
		});
		return _hasValues;
	}

	// get the prop value
	const propValue = wizardService.getValue(WizardId.createRule, makeKey(subjectProp, tripSemantics));

	if (valueExists(subjectProp, propValue)) {
		return false;
	}

	// this is the heart of the engine.
	if (isLeftSide(subjectProp)) {
		// is a left side prop, then it is not required if there are no other props with
		// values on either right side or the left side
		isRequired = hasValues([...leftSide, ...rightSide], wizardService, tripSemantics);
	} else {
		// is a right side prop then it is only required if there are other right side props with values
		isRequired = hasValues(rightSide, wizardService, tripSemantics);
	}

	return isRequired;
}
export function getValidatedElementsCommon(
	wizardService: WizardService,
	tripSemantics: TripSemantics,
): ValidatedElement[] {
	const timezonePipe = new DisplayTimezonePipe();
	// collect the state
	const startCondition =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_StartCondition, tripSemantics),
		) as string) || "";
	const timeZone =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_Timezone, tripSemantics),
		) as string) || "";

	const inspectionTypes =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_InspectionTypes, tripSemantics),
		) as MultiSelectChipOption[]) || [];
	const workPeriods = (
		(wizardService.getValue(
			WizardId.createRule,
			makeKey(StepProps.DefineDuty_WorkPeriods, tripSemantics),
		) as WorkPeriod[]) || []
	).filter(arrayFilterMap[StepProps.DefineDuty_WorkPeriods]);

	const zones =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_Zones, tripSemantics),
		) as MultiSelectChipOption[]) || [];
	const frequency =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_Frequency, tripSemantics),
		) as TripFrequencyUi) || [];
	const expireMinutes =
		(wizardService.getValue(
			WizardId.createRule,

			makeKey(StepProps.DefineDuty_ExpireMinutes, tripSemantics),
		) as string) || "15";
	const startConditionsLabels = {
		power: translateAndFormat(tripSemantics === TripSemantics.tripStart ? "power on" : "power off", "capitalize"),
		after_distance: translateAndFormat("vehicle moves 10 miles radius", "capitalize"),
		"selected-zone": translateAndFormat(
			tripSemantics === TripSemantics.tripStart ? "when selected zones exited" : "when selected zones entered",
			"capitalize",
		),
	};
	// validate
	let validatedElements: ValidatedElement[] = [
		{
			label: "inspection is expected when selected event occurs",
			value: startConditionsLabels[startCondition] ? startConditionsLabels[startCondition] : startCondition,
			error: isMissingAndRequired(StepProps.DefineDuty_StartCondition, wizardService, tripSemantics)
				? "Required"
				: null,
		},
		{
			label: "zones",
			value: zones
				.filter(zone => zone.selected)
				.map(zone => zone.label)
				.join(", "),
		},
		{
			label: "inspection types",
			value: inspectionTypes
				.filter(inspectionType => inspectionType.selected)
				.map(inspectionType => inspectionType.label)
				.join(", "),
			error: isMissingAndRequired(StepProps.DefineDuty_InspectionTypes, wizardService, tripSemantics)
				? "Required"
				: null,
		},
	];

	// add work period data requirements
	if (frequency === TripFrequencyUi.tripWithinWorkperiod || frequency === TripFrequencyUi.workPeriod) {
		validatedElements = [
			...validatedElements,
			{
				label: "expiration minutes",
				value: expireMinutes,
			},
			{
				label: "timezone",
				value: timezonePipe.transform(timeZone),
				error: isMissingAndRequired(StepProps.DefineDuty_Timezone, wizardService, tripSemantics)
					? "Required"
					: null,
			},
			{
				label: "work periods",
				value: workPeriods.length ? workPeriods.length + "" : "",
				error: isMissingAndRequired(StepProps.DefineDuty_WorkPeriods, wizardService, tripSemantics)
					? "Required"
					: null,
				extraHtml: (sanitizer: DomSanitizer) => {
					return sanitizer.bypassSecurityTrustHtml(extraHtml(workPeriods));
				},
			},
		];
	}

	// if all elements are empty (no value and no error), return []
	const hasAnyData = validatedElements.some(el => {
		// skip expireMinutes check if it equals the default
		if (el.label === "expiration minutes" && el.value === "15") {
			return false;
		}
		return (el.value && String(el.value).trim() !== "") || (el.error && String(el.error).trim() !== "");
	});

	return hasAnyData ? validatedElements : [];
}
